import { selectSelectedPlan } from "@/store/selectors/operator.selectors";
import { setSelectedPlan } from "@/store/slices/operator.slice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { OperatorPlan } from "@/store/types/get-operators";
import { Check, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface PlanItemProps {
  plan: OperatorPlan;
  operatorIcon: string;
  isSelected: boolean;
  onSelect: (sku: string) => void;
  index: number;
  totalPlans: number;
  showAllPlans: boolean;
}

export const PlanItem = ({
  plan,
  operatorIcon,
  isSelected,
  onSelect,
  index,
  totalPlans,
  showAllPlans,
}: PlanItemProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedPlan = useAppSelector(selectSelectedPlan);

  const [isHovering, setIsHovering] = useState(false);

  const handlePlanSelect = () => {
    if (selectedPlan === plan.sku) {
      dispatch(setSelectedPlan(null));
    } else {
      dispatch(setSelectedPlan(plan.sku));
    }
  };

  const getItemClassName = () => {
    const baseStyles =
      "flex justify-between items-center p-3 cursor-pointer select-none transition-colors";
    const hoverStyles = isSelected
      ? "hover:bg-red-400/10 hover:border-red-400/30"
      : "hover:bg-gray-50";
    const selectedStyles = isSelected ? "rounded-lg border border-primary" : "";
    const visibilityStyles = showAllPlans || isSelected ? "block" : "hidden";
    const borderStyles =
      !isSelected &&
      (index === 0
        ? "rounded-t-lg border border-gray-200"
        : "border-x border-b border-gray-200");
    const roundedBottomStyles =
      !isSelected && index === totalPlans - 1 ? "rounded-b-lg" : "";

    return `${baseStyles} ${hoverStyles} ${selectedStyles} ${visibilityStyles} ${borderStyles} ${roundedBottomStyles}`;
  };

  return (
    <>
      <div
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={() => {
          handlePlanSelect();
          onSelect(plan.sku);
        }}
        className={getItemClassName()}
      >
        <div className="flex items-center">
          <img
            className="h-16 sm:h-20 w-auto"
            src={operatorIcon}
            alt="Operator icon"
          />
          <div className="flex-col items-center content-center ml-4">
            <h3 className="font-semibold">{plan.name}</h3>
            {plan.promotion ? (
              <div>
                <h3 className="font-semibold text-red-700">
                  {t("operators.promoPrice", { price: plan.promotion.price })}
                </h3>
                <p className="text-sm text-gray-500">
                  {t("operators.promoThen", {
                    months: plan.promotion.months,
                    price: plan.price,
                  })}
                </p>
              </div>
            ) : (
              <div>
                <h3 className="font-semibold">{plan.price}</h3>
              </div>
            )}
          </div>
        </div>
        <div>
          {isSelected ? (
            isHovering ? (
              <Trash2 className="text-red-500" />
            ) : (
              <Check className="text-primary" />
            )
          ) : (
            <Plus />
          )}
        </div>
      </div>
    </>
  );
};
